<template>
  <div class="admin-wallet-transactions">
    <h2>Wallet Transactions</h2>
    <div class="filters row align-items-end">
      <div class="col-md-4">
        <card>
          <h4 slot="header" class="card-title">Select Range</h4>
          <el-select
            class="select-primary"
            size="large"
            @change="onRangeChange"
            placeholder="Select Ranges"
            v-model="rangeValue"
          >
            <el-option
              v-for="option in selects.ranges"
              class="select-primary"
              :value="option.value"
              :label="option.label"
              :key="option.label"
            />
          </el-select>
        </card>
      </div>
      <div class="col-md-3" v-if="showDatePicker">
        <card>
          <h4 slot="header" class="card-title">Start Date</h4>
          <base-input>
            <el-date-picker
              type="date"
              placeholder="Start Date"
              v-model="startDate"
              style="width: 100%;"
              @change="onDateChange"
            />
          </base-input>
        </card>
      </div>
      <div class="col-md-3" v-if="showDatePicker">
        <card>
          <h4 slot="header" class="card-title">End Date</h4>
          <base-input>
            <el-date-picker
              type="date"
              placeholder="End Date"
              v-model="endDate"
              style="width: 100%;"
              @change="onDateChange"
            />
          </base-input>
        </card>
      </div>
      <div class="col-md-2" v-if="showDatePicker">
        <card>
          <h4 slot="header" class="card-title">Search</h4>
          <button class="btn btn-primary w-100" @click="fetchTransactions">Filter</button>
        </card>
      </div>
    </div>
    <vue-good-table
      :columns="tableColumns"
      :rows="mappedTransactions"
      :pagination-options="{
        enabled: true,
        perPage: pagination.per_page
      }"
      :search-options="{
        enabled: true
      }"
      styleClass="table table-striped"
    >
    </vue-good-table>
  </div>
</template>

<script>
import { DatePicker, Select, Option } from 'element-ui';
import card from '@/components/Cards/Card.vue';
import VueResource from 'vue-resource';
import { api_url, datumMixin } from '../../env';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';

export default {
  name: 'WalletTransactions',
  mixins: [datumMixin],
  components: {
    VueGoodTable,
    card,
    'el-date-picker': DatePicker,
    'el-select': Select,
    'el-option': Option
  },
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      startDate: today,
      endDate: today,
      rangeValue: 'Today',
      showDatePicker: false,
      selects: {
        ranges: [
          { value: 'Today', label: 'Today' },
          { value: 'Yesterday', label: 'Yesterday' },
          { value: 'LastWeek', label: 'Last Week' },
          { value: 'WeekToDate', label: 'Week To Date' },
          { value: 'LastMonth', label: 'Last Month' },
          { value: 'MonthToDate', label: 'Month To Date' },
          { value: 'ThisYear', label: 'This Year' },
          { value: 'LastYear', label: 'Last Year' },
          { value: 'Custom', label: 'Custom Range' }
        ]
      },
      transactions: [],
      tableColumns: [
        { label: 'ID', field: 'index' },
        { label: 'Type', field: 'type' },
        { label: 'User', field: 'user_full_name' },
        { label: 'Amount', field: 'amount' },
        { label: 'Status', field: 'status' },
        { label: 'Reference', field: 'reference' },
        { label: 'Remarks', field: 'remarks' },
        { label: 'Date', field: 'updated_at' }
      ],
      loading: false,
      pagination: {
        total: 0,
        per_page: 50,
        current_page: 1,
      },
    };
  },
  computed: {
    mappedTransactions() {
      return this.transactions.map((tx, idx) => {
        let userFullName = '';
        if (tx.wallet && tx.wallet.user) {
          userFullName = `${tx.wallet.user.first_name || ''} ${tx.wallet.user.last_name || ''}`.trim();
        }
        return {
          ...tx,
          index: idx + 1,
          user_full_name: userFullName,
          updated_at: tx.updated_at ? new Date(tx.updated_at).toLocaleString() : '',
        };
      });
    },
    baseTableFields() {
      return this.tableColumns.map(col => col.field);
    }
  },
  watch: {
    rangeValue(val) {
      if (val === 'Custom') {
        this.showDatePicker = true;
      } else {
        this.showDatePicker = false;
        this.setRangeDates(val);
        this.fetchTransactions(1);
      }
    }
  },
  mounted() {
    this.setRangeDates(this.rangeValue);
    this.fetchTransactions(1);
  },
  methods: {
    setRangeDates(range) {
      const today = new Date();
      let startDate = today;
      let endDate = today;
      if (range === 'Today') {
        startDate = endDate = today;
      } else if (range === 'Yesterday') {
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 1);
        endDate = new Date(today);
        endDate.setDate(today.getDate() - 1);
      } else if (range === 'LastWeek') {
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        endDate = new Date(today);
        endDate.setDate(today.getDate() - 1);
      } else if (range === 'WeekToDate') {
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        endDate = today;
      } else if (range === 'LastMonth') {
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 30);
        endDate = new Date(today);
        endDate.setDate(today.getDate() - 1);
      } else if (range === 'MonthToDate') {
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 30);
        endDate = today;
      } else if (range === 'ThisYear') {
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = today;
      } else if (range === 'LastYear') {
        startDate = new Date(today.getFullYear() - 1, 0, 1);
        endDate = new Date(today.getFullYear() - 1, 11, 31);
      }
      this.startDate = this.formatDate(startDate);
      this.endDate = this.formatDate(endDate);
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toISOString().slice(0, 10);
    },
    onRangeChange() {
      // handled by watcher
    },
    onDateChange() {
      if (this.startDate && this.endDate) {
        this.fetchTransactions(1);
      }
    },
    async fetchTransactions(page = 1) {
      this.loading = true;

        const params = {
          start_date: this.startDate,
          end_date: this.endDate,
          page,
        };
        
        let headers = {
          "Authorization": "Bearer " + localStorage.getItem('token'),
        };
        this.$http.get(api_url + '/wallet/transactions', { params, headers })
        .then(response => response.body)
        .then(data => {
          if (data && data.data) {
            this.transactions = data.data;
            this.pagination = {
              total: data.total || 0,
              per_page: data.per_page || 50,
              current_page: data.current_page || 1,
            };
          } else {
            this.$notify({ type: 'danger', message: 'Failed to fetch transactions.' });
          }
        })
        .catch(() => {
          this.$notify({ type: 'danger', message: 'Failed to fetch transactions.' });
        })
        .finally(() => {
          this.loading = false;
        });
        
    },
    onPageChange(page) {
      this.fetchTransactions(page);
    },
  },

};
</script>

<style scoped>
.admin-wallet-transactions {
  padding: 2rem;
}
.filters {
  margin-bottom: 1rem;
}

</style>

<style>

.vgt-global-search{
  background: linear-gradient(#1e1e2f,#1e1e2f) !important;
  border : 1px solid #1e1e2f !important;
  float: right !important;

}

.vgt-wrap__footer {
  background: linear-gradient(#1e1e2f,#1e1e2f) !important;
  border : 1px solid #1e1e2f !important;
}

</style>
